<template>
    <div>
        <div>
            <div class="text-sm uppercase">Select Quantity</div>

            <div class="mt-4 flex items-center gap-3 lg:gap-4">
                <ConfiguratorQuantityInput v-model="quantity" :min="0" />

                <div
                    class="text-sm lg:text-base"
                    data-cy="configurator-quantity-description"
                >
                    {{ quantityDescription }}
                </div>
            </div>
            <InputHelpText v-if="helpText"> *{{ helpText }} </InputHelpText>
        </div>

        <transition name="fade" mode="out-in">
            <div
                v-if="crossSellProducts?.length"
                class="mt-8 border-t border-grind-200 pt-8"
            >
                <component
                    :is="crossSellProduct.key"
                    v-for="crossSellProduct in crossSellProducts"
                    :key="crossSellProduct.id"
                    :product="crossSellProduct"
                    @update:quantity="
                        updateCrossSells(crossSellProduct, $event)
                    "
                />
            </div>
        </transition>

        <div
            class="mb-8 mt-16 flex flex-col items-end justify-between gap-3 sm:flex-row md:flex-col lg:flex-row"
        >
            <div
                class="flex items-center gap-2 text-sm font-bold uppercase text-drip sm:text-sm md:text-sm lg:text-sm"
            >
                <template v-if="familyTurnaroundTime">
                    <Icon name="truck" class="text-[20px]" />
                    Ships within {{ familyTurnaroundTime }}
                </template>
            </div>

            <div class="flex items-center gap-1" data-cy="configurator-cost">
                <span class="text-3xl font-semibold">$</span>
                <span class="text-4xl font-bold">
                    {{ formatCurrency(totalPrice, false) }}
                </span>
            </div>
        </div>

        <div>
            <LoadingButton
                :loading="addingToCart"
                class="btn-primary w-full"
                @click="onAddToCart"
            >
                Add to Cart
            </LoadingButton>
        </div>
    </div>
</template>

<script lang="ts">
import BlankCanCrossSell from '~/components/page-building/product-view/components/cross-sells/BlankCanCrossSell.vue';

export default {
    components: {
        BlankCanCrossSell: BlankCanCrossSell,
    },
};
</script>

<script setup lang="ts">
import useAddToCart from '~/composables/cart/useAddToCart';
import { sendDataLayerEcommerceEvent } from '~/composables/useDataLayer';
import { Buyable, createBuyable } from '~/types/Buyable';
import { Image } from '~/types/miscellaneous';
import { CrossSellProduct, ProductData } from '~/types/product';
import { calculatePrice, scrollToId } from '~/utils/helpers';
import InputHelpText from '~/components/forms/components/InputHelpText.vue';

const emit = defineEmits(['showErrors']);

const props = defineProps<{
    productDatas: ProductData[];
    selectedProductData: ProductData | null;
    addToCartImage: Image | null;
    validationMessages: string[];
}>();

const state: {
    quantity: number;
    addingToCart: boolean;
    crossSells: CrossSellProduct[];
} = reactive({
    quantity: 0,
    addingToCart: false,
    crossSells: [],
});

console.log(props.productDatas);

const {
    familyQuantityDescription,
    familyQuantityDescriptionPlural,
    familyTurnaroundTime,
} = useFamilyProductDataLabels(
    computed(() => props.productDatas),
    computed(() => state.quantity),
);

if (props.selectedProductData && state.quantity === 0) {
    state.quantity = 1;
}

const updateCrossSells = (crossSellProduct, quantity) => {
    const index = state.crossSells.findIndex(
        (obj) => obj.id === crossSellProduct.id,
    );

    if (quantity > 0) {
        if (index === -1) {
            state.crossSells.push({
                id: crossSellProduct.id,
                quantity: quantity,
                price: crossSellProduct.price,
                name: crossSellProduct.name,
                image: {
                    src: crossSellProduct.image.src,
                    alt: crossSellProduct.image.alt,
                } as Image,
            } as CrossSellProduct);
        } else {
            state.crossSells[index].quantity = quantity;
        }
    } else if (index !== -1) {
        state.crossSells.splice(index, 1);
    }
};

const isCan = computed(() => {
    return props.selectedProductData?.productTypeShortLabel === 'can';
});

const quantityDescription = computed(() => {
    if (state.quantity === 0 || !props.selectedProductData) {
        return '';
    }

    if (props.selectedProductData?.uomCount > 1) {
        return 1 === state.quantity
            ? familyQuantityDescription.value
            : familyQuantityDescriptionPlural.value;
    }
    const productData = props.selectedProductData
        ? props.selectedProductData
        : props.productDatas[0];

    const label =
        1 === state.quantity
            ? productData.productTypeShortLabel
            : productData.productTypeShortLabelPlural;

    if (isCan.value) {
        return `${quantity.value} ${label} total, includes 1 peel & stick per ${productData?.productTypeShortLabel}`;
    }
    return `${quantity.value} ${label} total`;
});

const helpText = computed(() => {
    if (state.quantity === 0 || !props.selectedProductData) {
        return '';
    }
    if (props.selectedProductData?.uomCount > 1) {
        return `Sold in ${props.selectedProductData?.uomTypePlural} of ${props.selectedProductData?.uomCount}`;
    }

    return `Sold as ${props.selectedProductData.uomCount} individual ${props.selectedProductData.productTypeShortLabel}`;
});

const crossSellProducts = computed(() => {
    return props.productDatas
        .flatMap((product) => product.crossSellProducts)
        .reduce((unique, item) => {
            if (!unique.some((product) => product.id === item.id)) {
                unique.push(item);
            }
            return unique;
        }, []);
});

const totalCrossSellPrice = computed(() => {
    if (!state.crossSells.length) {
        return 0;
    }
    return state.crossSells.reduce((accumulator, currentItem) => {
        return accumulator + currentItem.quantity * currentItem.price;
    }, 0);
});

const totalPrice = computed(() => {
    return props.selectedProductData
        ? calculatePrice(
              state.quantity,
              props.selectedProductData.pricingTiers,
          ) + totalCrossSellPrice.value
        : 0;
});

watch(
    () => props.selectedProductData,
    () => {
        if (props.selectedProductData && state.quantity === 0) {
            state.quantity = 1;
        }
    },
);

const onAddToCart = async () => {
    if (props.validationMessages.length) {
        emit('showErrors', true);
        await nextTick();
        scrollToId('configurator-error');
        return;
    }

    if (!props.selectedProductData) {
        return;
    }

    state.addingToCart = true;

    const product = createBuyable({
        id: props.selectedProductData.id,
        quantity: state.quantity,
        image: {
            src: props.addToCartImage.src,
            alt: props.addToCartImage?.alt,
        } as Image,
        name: props.selectedProductData.marketingProductName,
    });

    if (props.selectedProductData) {
        const price = calculatePrice(
            state.quantity,
            props.selectedProductData.pricingTiers,
        );

        const items: any = [];
        items.push({
            item_id: props.selectedProductData.id,
            item_name: props.selectedProductData.name,
            index: 0,
            item_category: 'Stock',
            item_category2:
                props.selectedProductData.productTypeShortLabelPlural,
            item_category3:
                props.selectedProductData.configuratorAttributes?.material,
            item_list_name: 'Stock',
            price: price / state.quantity,
            quantity: state.quantity,
            is_sample:
                props.selectedProductData?.sku?.includes('SAMPLE') ?? false,
            color: props.selectedProductData.configuratorAttributes?.color,
            material:
                props.selectedProductData.configuratorAttributes?.material,
            finish: props.selectedProductData.configuratorAttributes?.finish,
            size: props.selectedProductData.configuratorAttributes?.size,
        });

        sendDataLayerEcommerceEvent('add_to_cart', '', {
            currency: 'USD',
            value: price,
            items: items,
        });
    }

    if (state.crossSells.length) {
        const products = state.crossSells.map(
            (item): Buyable =>
                createBuyable({
                    id: item.id,
                    image: item.image as Image,
                    quantity: item.quantity,
                    name: item.name,
                }),
        );

        await useAddToCart([product, ...products], true).finally(() => {
            state.addingToCart = false;
        });
    } else {
        await useAddToCart(product, true).finally(() => {
            state.addingToCart = false;
        });
    }
};

const { quantity, addingToCart } = toRefs(state);
</script>
